import {
  CostBasisCoveredLot,
  CostBasisMethodHolding,
} from '@interfaces/cost-basis/cost-basis.service.interface';
import {
  CostBasisPosition as _CostBasisPosition,
  FilteredCostBasisCoveredLot,
} from '@interfaces/cost-basis/cost-basis.service.interface';
import { CostBasisMethods } from '@interfaces/models';
import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import { Action, createReducer, on } from '@ngrx/store';
import { parseStrDefaultToZero } from '@sharedUtils';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';
import { formatBalance } from 'src/app/shared/helpers/selected-accounts';

import {
  CostBasisMethodReset,
  HydrateDivorceFromMultiIT,
  HydrateOrgToOrgFromMultiIT,
  LoadCostBasisLotsFail,
  LoadCostBasisLotsSuccess,
  LoadCostBasisMethodSuccess,
  UpdateInternalTransferAvgCostConversion,
  UpdateInternalTransferMinTaxConversion,
  UpdateSelectedPositions,
} from '../../actions';
import { UpdateSelectedPositionProperties } from '../../actions/cost-basis/cost-basis.actions';

export interface CostBasisPosition extends _CostBasisPosition {}
export interface CostBasisState {
  costBasisHoldingsMethod: (CostBasisMethodHolding | undefined)[];
  selectedPositions: CostBasisPosition[];
  costBasisCoveredLots: FilteredCostBasisCoveredLot[];
}

export const initialState: CostBasisState = {
  costBasisHoldingsMethod: [],
  selectedPositions: [],
  costBasisCoveredLots: [],
};

const configReducer = createReducer(
  initialState,
  on(LoadCostBasisMethodSuccess, (state, { payload }) => ({
    ...state,
    costBasisHoldingsMethod: payload,
    selectedPositions: addCostBasisToSelectedPositions(state, payload),
  })),
  on(CostBasisMethodReset, (state) => ({
    ...state,
    costBasisHoldingsMethod: [],
  })),
  on(LoadCostBasisLotsSuccess, (state, { payload }) => ({
    ...state,
    costBasisCoveredLots: payload,
  })),
  on(LoadCostBasisLotsFail, (state) => ({
    ...state,
    costBasisCoveredLots: [],
  })),
  on(UpdateSelectedPositionProperties, (state, { payload }) => {
    const updatedPositions = state.selectedPositions.map((position, positionIndex) => {
      return positionIndex === payload.index ? { ...position, ...payload.props } : position;
    });
    return {
      ...state,
      selectedPositions: updatedPositions,
    };
  }),
  on(UpdateSelectedPositions, (state, { payload }) => ({
    ...state,
    selectedPositions: preserveCostBasisMethod(state.selectedPositions, payload),
  })),
  on(HydrateDivorceFromMultiIT, (state, { payload }) => ({
    ...state,
    selectedPositions: payload.selectedPositions,
  })),
  on(HydrateOrgToOrgFromMultiIT, (state, { payload }) => ({
    ...state,
    selectedPositions: payload.selectedPositions,
  })),
  on(UpdateInternalTransferAvgCostConversion, (state, { payload }) => {
    // If the conversion method is FIFO, update all AVERAGE_COST positions to FIFO
    if (payload === AvgCostConversion.FIFO) {
      const updatedPositions = state.selectedPositions.map((position) =>
        position.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST
          ? { ...position, shownCostBasisMethod: CostBasisMethods.FIFO }
          : position,
      );
      return {
        ...state,
        selectedPositions: updatedPositions,
      };
    }
    // If the conversion method is AVGCOST, update all originally AVERAGE_COST positions to be AVERAGE_COST
    if (payload === AvgCostConversion.AVGCOST) {
      const updatedPositions = state.selectedPositions.map((position) =>
        position.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST
          ? { ...position, shownCostBasisMethod: CostBasisMethods.AVERAGE_COST }
          : position,
      );
      return {
        ...state,
        selectedPositions: updatedPositions,
      };
    }
    // Otherwise, return state unchanged
    return state;
  }),
  on(UpdateInternalTransferMinTaxConversion, (state, { payload }) => {
    // If the conversion method is FIFO, update all MIN_TAX positions to FIFO
    if (payload === MinTaxConversion.FIFO) {
      const updatedPositions = state.selectedPositions.map((position) =>
        position.selectedCostBasisMethod === CostBasisMethods.MIN_TAX
          ? { ...position, shownCostBasisMethod: CostBasisMethods.FIFO }
          : position,
      );
      return {
        ...state,
        selectedPositions: updatedPositions,
      };
    }
    // If the conversion method is MINTAX, update all originally MIN_TAX positions to be MIN_TAX
    if (payload === MinTaxConversion.MINTAX) {
      const updatedPositions = state.selectedPositions.map((position) =>
        position.selectedCostBasisMethod === CostBasisMethods.MIN_TAX
          ? { ...position, shownCostBasisMethod: CostBasisMethods.MIN_TAX }
          : position,
      );
      return {
        ...state,
        selectedPositions: updatedPositions,
      };
    }
    // Otherwise, return state unchanged
    return state;
  }),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: CostBasisState | undefined, action: Action) {
  return configReducer(state, action);
}

const addCostBasisToSelectedPositions = (
  state: CostBasisState,
  costBasisMethodHoldings: (CostBasisMethodHolding | undefined)[],
) => {
  if (!costBasisMethodHoldings?.length) {
    return state.selectedPositions;
  }
  const selectedPositionsWithCostBasis = state.selectedPositions.map((position) => {
    const costBasisMethod = costBasisMethodHoldings.find(
      (holding) => holding?.holdingIdentifier?.toString() === position.positionId,
    );
    const newPosition = { ...position };
    if (costBasisMethod) {
      if (!newPosition.selectedCostBasisMethod) {
        newPosition.selectedCostBasisMethod = costBasisMethod.costBasisMethodCode;
      }
      newPosition.shownCostBasisMethod = costBasisMethod.costBasisMethodCode;
    }
    return newPosition;
  });
  return selectedPositionsWithCostBasis;
};

/**
 * Carries forward cost basis method selections when positions are replaced, so
 * recalculations (e.g. estimated transfer value) don't wipe out the method that
 * LoadCostBasisMethodSuccess already resolved for a holding.
 */
const preserveCostBasisMethod = (
  currentPositions: CostBasisPosition[],
  incomingPositions: CostBasisPosition[],
): CostBasisPosition[] =>
  incomingPositions.map((incoming) => {
    if (incoming.selectedCostBasisMethod && incoming.shownCostBasisMethod) {
      return incoming;
    }
    const existing = currentPositions.find(
      (position) => position.positionId === incoming.positionId,
    );
    if (!existing) {
      return incoming;
    }
    return {
      ...incoming,
      selectedCostBasisMethod: incoming.selectedCostBasisMethod ?? existing.selectedCostBasisMethod,
      shownCostBasisMethod: incoming.shownCostBasisMethod ?? existing.shownCostBasisMethod,
    };
  });

export const getSelectedPositions = (state: CostBasisState) => state.selectedPositions;
export const getCostBasisHoldingsMethod = (state: CostBasisState) => state.costBasisHoldingsMethod;
export const getCostBasisLots = (state: CostBasisState) => state.costBasisCoveredLots;

export const filterLotsData = (lots: CostBasisCoveredLot[]): FilteredCostBasisCoveredLot[] =>
  lots.map((lot) => ({
    lotID: lot.lotID,
    quantity: lot.quantity,
    /**
     * The market value comes in unformatted. We might get values with more than 2 decimals, which might impact some of the validations.
     * We are doing the value processing here, so that every consuming part of the app gets the rounded value.
     */
    marketValue: parseStrDefaultToZero(formatBalance(lot.marketValue)),
    totalGainLoss: lot.totalGainLoss,
    aquiredDate: lot.aquiredDate,
    costperShare: lot.costperShare,
  }));
